import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import DocumentMeta from 'core/components/DocumentMeta'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import { RootState } from 'app/store'
import Text from 'core/elements/Text'
import useParams from 'core/hooks/useParams'
import { useSelector } from 'react-redux'
import ValidatedForm from 'core/components/validatedForm/ValidatedForm'
import SubmitButton from 'core/components/SubmitButton'
import { FormFieldCard } from 'core/components/validatedForm/FormFieldCard'
import PicklistField from 'core/components/validatedForm/DropdownField'
import RegionPicklist from './RegionPicklist'
import DomainPicklist from './DomainPicklist'
import ResourceTypePicklist from './ResourceTypePicklist'
import ActionPicklist from './ActionPicklist'
import TenantsPicklist from 'account/components/userManagement/common/TenantsPicklist'
import UsersPicklist from 'account/components/userManagement/users/UsersPicklist'
import ServicePicklist from './ServicePicklist'
import ResultPicklist from './ResultPicklist'
import TextField from 'core/components/validatedForm/TextField'
import DateTimePickerField from 'core/components/validatedForm/DateTimePickerField'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import QuantitySelectorField from 'core/components/validatedForm/QuantitySelectorField'
import { downloadAuditLogs, getAuditLogFilters } from './actions'
import { keys, prop } from 'ramda'
import downloadFile from 'core/utils/downloadFile'
import HintTooltip from 'core/elements/tooltip/HintTooltip'

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    display: 'grid',
    gap: 12,
    padding: '8px 0px 16px',
  },
  halfColumns: {
    marginTop: 20,
    display: 'grid',
    gap: 24,
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'baseline',
  },
  actions: {
    marginTop: 24,
    paddingTop: 24,
    borderTop: `1px solid ${theme.palette.grey[200]}`,
    display: 'flex',
    justifyContent: 'start',
  },
  button: {
    marginTop: 8,
  },
  quantitySelector: {
    display: 'grid',
    gridTemplateColumns: 'max-content',
    gridGap: 8,
    // marginTop: 8,
  },
  quantitySelectorLabel: {
    marginLeft: 4,
  },
  label: {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content',
    gap: 8,
    alignItems: 'center',
  },
  info: {
    display: 'grid',
    cursor: 'help',
    transition: 'color .2s ease',
    color: theme.components.input.label.hint,
    '& i': {
      cursor: 'help',
      marginRight: 8,
      transition: 'color .2s ease',
      color: theme.components.input.label.hint,
      '&:hover': { color: theme.palette.grey[700] },
    },
  },
}))

const keyToParamMap = {
  region: 'region',
  service: 'service',
  domain: 'domain',
  outcome: 'outcome',
  resourceType: 'resource_type',
  action: 'action',
  tenant: 'project_id',
  user: 'user_id',
  limit: 'limit',
  startDate: 'start_time',
  endDate: 'end_time',
}

export default function AuditLogsPage() {
  const classes = useStyles()

  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const { domain } = session
  const { prefs } = useScopedPreferences()
  const { currentTenant, currentRegion } = prefs

  const currentDate = useMemo(() => {
    return new Date()
  }, [])

  const defaultParams = {
    region: null,
    service: null,
    outcome: null,
    resourceType: null,
    action: null,
    tenant: null,
    user: null,
    limit: null,
    startDate: null,
    endDate: null,
    result: null,
    domain: null,
    showOptionalFilters: false,
  }
  const [filterValues, setFilterValues] = useState<any>({})
  const [preparingDownload, setPreparingDownload] = useState(false)
  const [downloaded, setDownloaded] = useState(false)
  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)

  useEffect(() => {
    const makeCalls = async () => {
      const response = await getAuditLogFilters()
      const servicesList = keys(response?.services)
      const resourceTypesByService = response?.services
      setFilterValues({
        ...response,
        services: servicesList,
        resourceTypesByService,
      })
    }
    makeCalls()
  }, [])

  useEffect(() => {
    updateParams({
      domain: domain?.id,
      tenant: currentTenant,
      region: currentRegion,
    })
  }, [domain, currentTenant, currentRegion])

  useEffect(() => {
    if (downloaded) {
      setTimeout(() => {
        setDownloaded(false)
      }, 2000)
    }
  }, [downloaded])

  const handleFormSubmit = useCallback(async () => {
    const paramKeys = keys(params)
    const paramPairs = paramKeys.map((key) => ({
      key: keyToParamMap[key],
      value: params[key],
    }))
    const specifiedPairs = paramPairs.filter((pair) => !!pair.key && !!pair.value)
    try {
      setPreparingDownload(true)
      const response = await downloadAuditLogs({ urlParams: specifiedPairs })
      const dateString = new Date().toISOString()
      const filename = `audit_log_${dateString}.ndjson`
      downloadFile({
        filename,
        contents: (response as unknown) as string,
        contentType: 'text/plain',
      })
      setDownloaded(true)
    } catch (e) {
      console.log(e)
    } finally {
      setPreparingDownload(false)
    }
  }, [params])

  return (
    <>
      <DocumentMeta title="Download Audit Logs" breadcrumbs />
      <ValidatedForm onSubmit={handleFormSubmit}>
        <FormFieldCard title={<Text variant="subtitle1">Download Audit Logs</Text>}>
          <div className={classes.card}>
            <Text variant="body2">
              Select from the following filters to download the desired audit logs. You must select
              a region, domain, tenant, and service to download logs.
            </Text>
            <Text variant="body2">
              To narrow down the desired audit logs, you may add more filters by toggling{' '}
              <b>Use Optional Filters</b>. If no start time is provided, logs up until the past 24
              hours will be included. Only logs that satisfy every filter will be downloaded.
            </Text>
            <div className={classes.halfColumns}>
              <PicklistField
                id="region"
                label="Region"
                onChange={getParamsUpdater('region')}
                value={params.region}
                DropdownComponent={RegionPicklist}
                regions={filterValues?.regions}
                required
              />
              <PicklistField
                id="domain"
                label="Domain"
                onChange={getParamsUpdater('domain')}
                value={params.domain}
                domains={filterValues?.domains}
                DropdownComponent={DomainPicklist}
                required
              />
              <PicklistField
                id="tenant"
                label="Tenant"
                // @ts-ignore
                onChange={getParamsUpdater('tenant')}
                value={params.tenant}
                DropdownComponent={TenantsPicklist}
                required
              />
              <PicklistField
                id="service"
                label="Service"
                onChange={getParamsUpdater('service')}
                value={params.service}
                DropdownComponent={ServicePicklist}
                services={filterValues?.services}
                required
              />
              <ToggleSwitchField
                id="showOptionalFilters"
                value={params.showOptionalFilters}
                onChange={getParamsUpdater('showOptionalFilters')}
                label="Use Optional Filters"
              />
              <div />
              {params.showOptionalFilters && (
                <>
                  <PicklistField
                    id="resourceType"
                    label="Resource Type"
                    info="Only resource types belonging to the selected service will be shown."
                    onChange={getParamsUpdater('resourceType')}
                    value={params.resourceType}
                    resourceTypes={filterValues?.resourceTypesByService?.[params.service] || []}
                    DropdownComponent={ResourceTypePicklist}
                    placeholder={params.service ? 'Select an option' : 'Select a Service'}
                  />
                  <PicklistField
                    id="user"
                    label="User"
                    onChange={getParamsUpdater('user')}
                    value={params.user}
                    DropdownComponent={UsersPicklist}
                    compact
                  />
                  <PicklistField
                    id="action"
                    label="Action"
                    onChange={getParamsUpdater('action')}
                    value={params.action}
                    actions={filterValues?.action_names}
                    DropdownComponent={ActionPicklist}
                  />
                  <PicklistField
                    id="result"
                    label="Result"
                    onChange={getParamsUpdater('result')}
                    value={params.result}
                    results={filterValues?.action_results}
                    DropdownComponent={ResultPicklist}
                  />
                  <DateTimePickerField
                    id="startDate"
                    label="Start Date"
                    onChange={getParamsUpdater('startDate')}
                    value={params.startDate}
                    placeholder="Select start date"
                    maxDate={currentDate}
                  />
                  <DateTimePickerField
                    id="endDate"
                    label="End Date"
                    onChange={getParamsUpdater('endDate')}
                    value={params.endDate}
                    placeholder="Select end date"
                    maxDate={currentDate}
                  />
                  <div className={classes.quantitySelector}>
                    <div className={classes.label}>
                      <Text className={classes.quantitySelectorLabel} variant="caption1">
                        Limit
                      </Text>
                      <HintTooltip
                        message="The maximum number of logs to retrieve"
                        className={classes.info}
                      />
                    </div>
                    <QuantitySelectorField
                      id="limit"
                      value={params.limit}
                      onChange={getParamsUpdater('limit')}
                      min={1}
                      data-testid="limit-input"
                    />
                  </div>
                </>
              )}
            </div>
            <div className={classes.actions}>
              <SubmitButton
                className={classes.button}
                icon={downloaded ? 'check' : 'download'}
                disabled={preparingDownload || downloaded}
              >
                {downloaded
                  ? 'Downloaded Successfully'
                  : preparingDownload
                  ? 'Preparing Download...'
                  : 'Download Logs'}
              </SubmitButton>
            </div>
          </div>
        </FormFieldCard>
      </ValidatedForm>
    </>
  )
}
